/**
 * DomainRegistryProvider
 *
 * Loads all domain registry YAML files at app startup and provides them
 * via React context. This is the foundation for config-first architecture —
 * all endpoints, events, cache strategies, and UI paths come from YAML.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  ReactNode,
} from "react";

import debugLogger from "../utils/debugLogger";

import {
  DomainRegistry,
  RegistryEndpoint,
  RegistryCacheStrategy,
  RegistryEvent,
  loadAllDomainRegistries,
  loadDomainRegistry,
} from "./domainRegistry";

export type DomainName =
  "auth" | "organization" | "project" | "application" | "user" | "resource";

interface DomainRegistryContextValue {
  /** All loaded domain registries keyed by domain name. */
  registries: Map<string, DomainRegistry>;
  /** Whether all registries have finished loading. */
  isLoaded: boolean;
  /** Error that occurred during loading, if any. */
  error: string | null;
  /** Get a single domain's registry. Returns undefined if not loaded. */
  getRegistry: (domain: DomainName) => DomainRegistry | undefined;
  /** Get a specific endpoint from a domain. */
  getEndpoint: (
    domain: DomainName,
    endpointName: string,
  ) => RegistryEndpoint | undefined;
  /** Get all endpoints for a domain. */
  getEndpoints: (domain: DomainName) => RegistryEndpoint[];
  /** Get UI paths for a domain. */
  getUIPaths: (domain: DomainName) => Record<string, string>;
  /** Get cache strategies for a domain. */
  getCacheStrategies: (
    domain: DomainName,
  ) => Record<string, RegistryCacheStrategy>;
  /** Get events a domain emits. */
  getEmittedEvents: (domain: DomainName) => RegistryEvent[];
  /** Get events a domain listens to. */
  getListenedEvents: (domain: DomainName) => RegistryEvent[];
}

const DomainRegistryContext = createContext<DomainRegistryContextValue | null>(
  null,
);

interface DomainRegistryProviderProps {
  children: ReactNode;
}

export const DomainRegistryProvider: React.FC<DomainRegistryProviderProps> = ({
  children,
}) => {
  const [registries, setRegistries] = useState<Map<string, DomainRegistry>>(
    new Map(),
  );
  const [isLoaded, setIsLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const loaded = await loadAllDomainRegistries();
        if (!cancelled) {
          setRegistries(loaded);
          setIsLoaded(true);
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : "Failed to load registries";
          debugLogger.error("DomainRegistryProvider:", message);
          setError(message);
          setIsLoaded(true); // still mark loaded so app doesn't hang
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, []);

  const getRegistry = useCallback(
    (domain: DomainName) => registries.get(domain),
    [registries],
  );

  const getEndpoint = useCallback(
    (domain: DomainName, endpointName: string) => {
      const reg = registries.get(domain);
      return reg?.endpoints?.find((e) => e.name === endpointName);
    },
    [registries],
  );

  const getEndpoints = useCallback(
    (domain: DomainName) => {
      const reg = registries.get(domain);
      return reg?.endpoints ?? [];
    },
    [registries],
  );

  const getUIPaths = useCallback(
    (domain: DomainName) => {
      const reg = registries.get(domain);
      return reg?.ui?.paths ?? {};
    },
    [registries],
  );

  const getCacheStrategies = useCallback(
    (domain: DomainName) => {
      const reg = registries.get(domain);
      return reg?.cache?.strategies ?? {};
    },
    [registries],
  );

  const getEmittedEvents = useCallback(
    (domain: DomainName) => {
      const reg = registries.get(domain);
      return reg?.events?.emits ?? [];
    },
    [registries],
  );

  const getListenedEvents = useCallback(
    (domain: DomainName) => {
      const reg = registries.get(domain);
      return reg?.events?.listens ?? [];
    },
    [registries],
  );

  const value = useMemo<DomainRegistryContextValue>(
    () => ({
      registries,
      isLoaded,
      error,
      getRegistry,
      getEndpoint,
      getEndpoints,
      getUIPaths,
      getCacheStrategies,
      getEmittedEvents,
      getListenedEvents,
    }),
    [
      registries,
      isLoaded,
      error,
      getRegistry,
      getEndpoint,
      getEndpoints,
      getUIPaths,
      getCacheStrategies,
      getEmittedEvents,
      getListenedEvents,
    ],
  );

  return (
    <DomainRegistryContext.Provider value={value}>
      {children}
    </DomainRegistryContext.Provider>
  );
};

/**
 * Access the full domain registry context.
 */
export const useDomainRegistries = (): DomainRegistryContextValue => {
  const context = useContext(DomainRegistryContext);
  if (!context) {
    throw new Error(
      "useDomainRegistries must be used within a DomainRegistryProvider",
    );
  }
  return context;
};

/**
 * Access a single domain's registry.
 * Returns the registry, loading state, and helper functions scoped to that domain.
 */
export function useDomainRegistry(domain: DomainName) {
  const ctx = useDomainRegistries();

  const registry = ctx.getRegistry(domain);
  const endpoints = ctx.getEndpoints(domain);
  const uiPaths = ctx.getUIPaths(domain);
  const cacheStrategies = ctx.getCacheStrategies(domain);
  const emittedEvents = ctx.getEmittedEvents(domain);
  const listenedEvents = ctx.getListenedEvents(domain);

  const getEndpoint = useCallback(
    (name: string) => ctx.getEndpoint(domain, name),
    [ctx, domain],
  );

  return useMemo(
    () => ({
      registry,
      isLoaded: ctx.isLoaded,
      endpoints,
      uiPaths,
      cacheStrategies,
      emittedEvents,
      listenedEvents,
      getEndpoint,
    }),
    [
      registry,
      ctx.isLoaded,
      endpoints,
      uiPaths,
      cacheStrategies,
      emittedEvents,
      listenedEvents,
      getEndpoint,
    ],
  );
}

// Re-export for convenience
export { loadDomainRegistry, loadAllDomainRegistries };
